import { Text } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Divider, Stack } from "@mui/material";
import { type Agent } from "@src/appV2/Agents";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type UseQueryResult } from "@tanstack/react-query";
import { useRouteMatch } from "react-router-dom";

import { type ShiftBlockDetail } from "../api/useGetShiftBlock";
import { SHIFT_BLOCK_ACTIONS, transformIntoShiftBlockWithDetails } from "../utils";
import { BookShiftBlockButton } from "./BookShiftBlockButton";
import { FacilityChatButtonContainer } from "./FacilityChatButtonContainer";
import { ShiftBlockFacilityDetails } from "./ShiftBlockFacilityDetails";
import { ShiftBlockHeader } from "./ShiftBlockHeader";
import { ShiftBlockShiftDetails } from "./ShiftBlockShiftDetails";

interface ShiftBlockDetailProps {
  agent: Agent;
  shiftBlock: ShiftBlockDetail;
  refetchShiftBlock: UseQueryResult<ShiftBlockDetail>["refetch"];
}

export function ShiftBlockDetails(props: ShiftBlockDetailProps) {
  const { agent, shiftBlock, refetchShiftBlock } = props;
  const { path: currentRouterUrl } = useRouteMatch();

  const shiftBlockWithMappedDetails = transformIntoShiftBlockWithDetails(
    shiftBlock.data,
    shiftBlock.included
  );
  const { id: shiftBlockId, facility, shifts, isShiftBlockAssigned } = shiftBlockWithMappedDetails;

  useLogEffect(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
    action: SHIFT_BLOCK_ACTIONS.BLOCK_DETAILS_VIEWED,
    shiftBlockId,
    isShiftBlockAssigned,
    location: currentRouterUrl,
  });

  return (
    <Stack spacing={2}>
      <ShiftBlockHeader
        shiftBlockWithDetails={shiftBlockWithMappedDetails}
        agentTimezone={agent.tmz}
      />
      <ShiftBlockFacilityDetails facility={facility} isShiftBlockAssigned={isShiftBlockAssigned} />
      {isDefined(shiftBlock.data.attributes.notesForProfessionals) && (
        <>
          <Stack spacing={0.5}>
            <Text
              sx={{
                fontWeight: 600,
              }}
            >
              Block Information
            </Text>
            <Text
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.primary,
                opacity: 0.8,
              }}
            >
              {shiftBlock.data.attributes.notesForProfessionals}
            </Text>
          </Stack>

          <Divider />
        </>
      )}
      {isDefined(agent) && (
        <FacilityChatButtonContainer
          shiftBlockId={shiftBlockId}
          agentId={agent.userId}
          facilityId={facility.id}
          isShiftBlockAssigned={isShiftBlockAssigned}
        />
      )}

      <ShiftBlockShiftDetails
        shiftBlockWithDetails={shiftBlockWithMappedDetails}
        agentTimezone={agent.tmz}
      />

      {!isShiftBlockAssigned && (
        <Box sx={{ position: "absolute", bottom: "24px", left: "24px", right: "24px" }}>
          <BookShiftBlockButton
            facilityId={shiftBlock.data.attributes.facilityId}
            agentId={agent.userId}
            shiftBlockId={shiftBlock.data.id}
            shifts={shifts}
            onBookingShiftBlock={refetchShiftBlock}
          />
        </Box>
      )}
    </Stack>
  );
}
